import React, { Fragment, useEffect, useRef, useState } from "react";
import axios from "../../api";
import {
  Box,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Lock, Edit } from "@mui/icons-material";
import SignaturePad from "react-signature-canvas";
import { toast } from "react-toastify";

// The device fields an employee may correct if inventory doesn't match reality.
const ASSET_FIELDS = [
  ["assetTag", "Asset Tag"],
  ["serialNumber", "Serial Number"],
  ["Model", "Model"],
  ["ModelNo", "Model No"],
  ["Manufacturer", "Manufacturer"],
  ["Specifications", "Specifications"],
];

// Standard kit typically issued with a laptop — pre-checked on acknowledge.
const STANDARD_ISSUE_ACCESSORIES = [
  "Mouse",
  "Headphones",
  "External Monitor",
  "Monitor Cable",
];

const FALLBACK_ACCESSORIES = [
  "Charger",
  "Bag",
  "Mouse",
  "Headphones",
  "External Monitor",
  "Monitor Cable",
  "Keyboard",
];

const pickStandardAccessories = (options) => {
  const opts = options?.length ? options : FALLBACK_ACCESSORIES;
  return STANDARD_ISSUE_ACCESSORIES.map((wanted) => {
    const match = opts.find(
      (o) => String(o).toLowerCase() === wanted.toLowerCase()
    );
    return match || null;
  }).filter(Boolean);
};

function AcknowledgeDialog({ open, onClose, asset, settings, onSubmitted }) {
  const user = JSON.parse(localStorage.getItem("adminUser") || "null");
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const [assetForm, setAssetForm] = useState({});
  const [form, setForm] = useState({
    EmpName: "",
    EmpID: "",
    email: "",
    phone: "",
    address: "",
    Manager: "",
    Department: "",
    Title: "",
    City: "",
    State: "",
    Country: "",
    Zip: "",
    accessories: [],
    AccessoriesExtra: "",
    termsAccepted: false,
    idProofFile: null,
    assetImages: [],
  });
  const [signatureType, setSignatureType] = useState("upload");
  const [signatureFile, setSignatureFile] = useState(null);
  const signaturePadRef = useRef(null);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [fieldOptions, setFieldOptions] = useState({
    manufacturers: [],
    specifications: [],
  });

  const accessoriesOptions = settings?.formOptions?.accessories?.length
    ? settings.formOptions.accessories
    : FALLBACK_ACCESSORIES;
  const termsOptions = settings?.terms?.length ? settings.terms : null;

  useEffect(() => {
    if (!open) return;
    axios
      .get("/assets/field-options")
      .then((res) =>
        setFieldOptions({
          manufacturers: res.data.result?.manufacturers || [],
          specifications: res.data.result?.specifications || [],
        })
      )
      .catch(() => {
        setFieldOptions({
          manufacturers: settings?.formOptions?.manufacturers || [],
          specifications: settings?.formOptions?.specifications || [],
        });
      });
  }, [open, settings?.formOptions?.manufacturers, settings?.formOptions?.specifications]);

  useEffect(() => {
    if (!open || !asset) return;
    setEditing(false);
    setSignatureType("upload");
    setSignatureFile(null);
    const me = JSON.parse(localStorage.getItem("adminUser") || "null");
    setForm({
      EmpName: me?.name || asset.assignedTo?.empName || "",
      EmpID: me?.empId || asset.assignedTo?.empId || "",
      email: me?.email || asset.assignedTo?.email || "",
      phone: "",
      address: "",
      Manager: "",
      Department: "",
      Title: "",
      City: "",
      State: "",
      Country: "",
      Zip: "",
      // Pre-select the usual issued kit; employee can uncheck if not received.
      accessories: pickStandardAccessories(
        settings?.formOptions?.accessories
      ),
      AccessoriesExtra: "",
      termsAccepted: false,
      idProofFile: null,
      assetImages: [],
    });
    setAssetForm({
      assetTag: asset.assetTag || "",
      serialNumber: asset.serialNumber || "",
      Model: asset.model || "",
      ModelNo: asset.modelNo || "",
      Manufacturer: asset.manufacturer || "",
      Specifications: asset.specifications || "",
    });

    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      },
      () => {}
    );
    const ipLookupUrl =
      process.env.REACT_APP_IP_LOOKUP_URL || "https://api.ipify.org?format=json";
    fetch(ipLookupUrl)
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch(() => {});
  }, [open, asset, settings?.formOptions?.accessories]);

  const validateImage = (file) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF files are not allowed. Please upload an image.");
      return false;
    }
    if (!allowed.includes(file.type)) {
      toast.error("Only image files (JPEG, PNG, GIF, WebP) are allowed.");
      return false;
    }
    return true;
  };

  const handleFile = (e) => {
    const { name, files } = e.target;
    if (!files || !files.length) return;
    if (name === "assetImages") {
      for (const f of files) if (!validateImage(f)) return;
      setForm((p) => ({ ...p, assetImages: files }));
    } else if (name === "signatureFile") {
      if (!validateImage(files[0])) return;
      setSignatureFile(files[0]);
    } else {
      if (!validateImage(files[0])) return;
      setForm((p) => ({ ...p, [name]: files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.termsAccepted) return toast.warning("Please accept the terms and conditions.");
    if (!form.idProofFile) return toast.warning("Please upload your ID proof.");
    if (!String(form.email || "").trim()) {
      return toast.warning("Please enter your official Email ID.");
    }
    if (!String(form.EmpName || "").trim()) {
      return toast.warning("Please enter your Employee Name.");
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("assetId", asset._id);

      Object.entries(form).forEach(([key, val]) => {
        if (key === "assetImages") {
          for (const f of val) data.append("assetImages", f);
        } else if (key === "accessories") {
          data.append("accessories", Array.isArray(val) ? val.join(", ") : val);
        } else if (key !== "idProofFile") {
          data.append(key, val);
        }
      });
      // Asset detail fields (possibly corrected by the employee)
      Object.entries(assetForm).forEach(([k, v]) => data.append(k, v ?? ""));

      data.append("idProofFile", form.idProofFile);
      data.append("latitude", latitude);
      data.append("longitude", longitude);
      data.append("ipAddress", ipAddress);

      if (signatureType === "draw") {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
          setSubmitting(false);
          return toast.warning("Please draw your signature.");
        }
        const url = signaturePadRef.current.toDataURL("image/png");
        const blob = await (await fetch(url)).blob();
        data.append("signatureFile", new File([blob], "signature.png", { type: "image/png" }));
      } else {
        if (!signatureFile) {
          setSubmitting(false);
          return toast.warning("Please upload your signature.");
        }
        data.append("signatureFile", signatureFile);
      }

      const resp = await axios.post("/undertaking/acknowledge", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp.data.hasDiscrepancy) {
        toast.info("Submitted. Your corrections were flagged for IT review.");
      } else {
        toast.success("Acknowledgment submitted!");
      }
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        Acknowledge device
        <Chip
          size="small"
          label={asset.assetTag || asset.serialNumber || "device"}
          color="primary"
        />
      </DialogTitle>
      <DialogContent dividers>
        <form id="ack-form" onSubmit={handleSubmit}>
          {/* Device details */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Typography variant="h6">Device details</Typography>
            <Chip
              size="small"
              icon={editing ? <Edit sx={{ fontSize: 14 }} /> : <Lock sx={{ fontSize: 14 }} />}
              label={editing ? "Editing" : "From records"}
              color={editing ? "warning" : "default"}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              onClick={() => {
                if (editing && asset) {
                  setAssetForm({
                    assetTag: asset.assetTag || "",
                    serialNumber: asset.serialNumber || "",
                    Model: asset.model || "",
                    ModelNo: asset.modelNo || "",
                    Manufacturer: asset.manufacturer || "",
                    Specifications: asset.specifications || "",
                  });
                }
                setEditing((v) => !v);
              }}
            >
              {editing ? "Cancel edit" : "Details don't match?"}
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {editing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Correct any wrong details below. Your changes are recorded and sent to IT for
              review — they won't overwrite the records automatically.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {ASSET_FIELDS.map(([name, label]) => {
              if (name === "Manufacturer" || name === "Specifications") {
                const options =
                  name === "Manufacturer"
                    ? fieldOptions.manufacturers
                    : fieldOptions.specifications;
                return (
                  <Grid item xs={12} sm={6} key={name}>
                    <Autocomplete
                      freeSolo
                      disabled={!editing}
                      options={options}
                      value={assetForm[name] || ""}
                      onChange={(_, value) =>
                        setAssetForm((p) => ({ ...p, [name]: value || "" }))
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === "input" || reason === "clear") {
                          setAssetForm((p) => ({ ...p, [name]: value || "" }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={label}
                          placeholder={editing ? "Select or type" : undefined}
                        />
                      )}
                    />
                  </Grid>
                );
              }
              return (
                <Grid item xs={12} sm={6} key={name}>
                  <TextField
                    fullWidth
                    label={label}
                    value={assetForm[name] || ""}
                    onChange={(e) =>
                      setAssetForm((p) => ({ ...p, [name]: e.target.value }))
                    }
                    disabled={!editing}
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* Your details */}
          <Typography variant="h6" gutterBottom>
            Your details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Employee Name"
                value={form.EmpName}
                onChange={(e) => setForm((p) => ({ ...p, EmpName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID"
                value={form.EmpID}
                onChange={(e) => setForm((p) => ({ ...p, EmpID: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Official Email ID"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                helperText={
                  user?.email
                    ? "Pre-filled from your login. Update only if your official email ID differs."
                    : "Enter your official organization email ID."
                }
              />
            </Grid>
            {[
              ["phone", "Phone", true],
              ["address", "Address", true],
              ["Department", "Department", false],
              ["Title", "Title", false],
              ["Manager", "Manager", false],
              ["City", "City", false],
              ["State", "State", false],
              ["Country", "Country", false],
              ["Zip", "Zip", false],
            ].map(([name, label, required]) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField
                  fullWidth
                  required={required}
                  label={label}
                  value={form[name]}
                  onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                SelectProps={{ multiple: true }}
                label="Accessories received"
                value={form.accessories}
                helperText="Typical kit is pre-selected (Mouse, Headphones, External Monitor, Monitor Cable). Uncheck anything not received."
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    accessories:
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                  }))
                }
              >
                {accessoriesOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Other accessories"
                value={form.AccessoriesExtra}
                onChange={(e) => setForm((p) => ({ ...p, AccessoriesExtra: e.target.value }))}
              />
            </Grid>
          </Grid>

          {/* Uploads */}
          <Typography variant="h6" gutterBottom>
            Uploads
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth>
                {form.idProofFile ? "ID Proof ✓" : "Upload ID Proof (image)"}
                <input hidden type="file" name="idProofFile" accept="image/*" onChange={handleFile} />
              </Button>
              {form.idProofFile && (
                <Typography variant="caption" color="text.secondary">
                  {form.idProofFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth>
                {form.assetImages?.length
                  ? `Asset Images (${form.assetImages.length}) ✓`
                  : "Upload Asset Images"}
                <input hidden type="file" name="assetImages" accept="image/*" multiple onChange={handleFile} />
              </Button>
            </Grid>
          </Grid>

          {/* Signature */}
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
            Signature
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <FormLabel component="legend">Signature type</FormLabel>
            <RadioGroup row value={signatureType} onChange={(e) => setSignatureType(e.target.value)}>
              <FormControlLabel value="upload" control={<Radio />} label="Upload" />
              <FormControlLabel value="draw" control={<Radio />} label="Draw" />
            </RadioGroup>
          </FormControl>
          {signatureType === "upload" ? (
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" component="label">
                {signatureFile ? "Signature ✓" : "Upload Signature (image)"}
                <input hidden type="file" name="signatureFile" accept="image/*" onChange={handleFile} />
              </Button>
              {signatureFile && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {signatureFile.name}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ border: "1px solid #cbd5e1", borderRadius: 2, width: "fit-content" }}
                role="img"
                aria-label="Draw your signature in this pad"
              >
                <SignaturePad
                  ref={signaturePadRef}
                  canvasProps={{
                    width: 400,
                    height: 160,
                    "aria-label": "Signature drawing pad",
                  }}
                />
              </Box>
              <Button
                size="small"
                aria-label="Clear signature"
                onClick={() => signaturePadRef.current?.clear()}
                sx={{ mt: 1 }}
              >
                Clear
              </Button>
            </Box>
          )}

          {/* Terms */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 2 }}>
            <FormControlLabel
              sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
              control={
                <Checkbox
                  checked={form.termsAccepted}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, termsAccepted: e.target.checked }))
                  }
                  inputProps={{ "aria-label": "Agree to Terms and Conditions" }}
                />
              }
              label="I agree to the Terms & Conditions"
            />
            <Button size="small" sx={{ mt: 1 }} onClick={() => setOpenTerms(true)}>
              View
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Location: Lat {latitude || "--"}, Long {longitude || "--"} | IP: {ipAddress || "--"}
          </Typography>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" form="ack-form" variant="contained" disabled={submitting}>
          {submitting ? "Submitting…" : "Acknowledge & Submit"}
        </Button>
      </DialogActions>

      <Dialog open={openTerms} onClose={() => setOpenTerms(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Terms and Conditions</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" component="div">
            {termsOptions ? (
              termsOptions.map((line, i) => (
                <Fragment key={i}>
                  {i + 1}. {line}
                  <br />
                </Fragment>
              ))
            ) : (
              <>
                1. This device is a company asset and should be used for official purposes.
                <br />
                2. Maintain the asset in good condition and report issues to IT.
                <br />
                3. Return the asset upon separation from the company.
                <br />
                4. You are liable for misuse, loss, or damage.
                <br />
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTerms(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={submitting} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 2 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default AcknowledgeDialog;
